"use strict";
/**
 * Каталог прав — ЕДИНСТВЕННЫЙ объявленный источник (RBAC-2.3 AC-1, AD-2.7).
 *
 * До этой стори у прав не было источника вообще: референсная схема
 * `auth_permissions_schema.sql` заводила таблицу `permissions` и **ни одной строки**, а
 * 81 право наросло ad-hoc-миграциями поверх неизвестного начального сида. Приговор без
 * такого источника — снимок, который разойдётся с базой на первой же миграции, ровно так,
 * как разошлись эти 81.
 *
 * Правило, ради которого файл существует:
 *
 *   **право существует тогда и только тогда, когда его кто-то спрашивает.**
 *
 * Отсюда форма: у каждого имени в списке ниже назван его потребитель (`file:line`), и
 * сторож `permissionsInGates.guard.test.ts` проверяет это в обе стороны — каталог без
 * гейта и гейт без каталога одинаково красят тест. Заводить право «на будущее» здесь
 * нельзя: будущее заведёт его вместе с гейтом, и это дешевле, чем полгода хранить
 * обещание. Так уже было: 72 права из 81 были механическим кросс-произведением
 * «ресурс × действие», и у 68 из них потребителя не появилось никогда.
 *
 * ⚠ Право — не строка, а пара `(resource, action)`; `name` в базе лежит рядом как
 * денормализация (`UNIQUE(resource_id, action_id)`). Поэтому каталог объявляет ВСЕ ТРИ
 * таблицы: `PERMISSIONS` + `PERMISSION_PARTS` + производные `PERMISSION_RESOURCES` /
 * `PERMISSION_ACTIONS`. Объявить одни имена и бросить скелет значит оставить то, из чего
 * матрицу развернут заново одной строкой.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.PERMISSION_PARTS = exports.PERMISSION_ACTION_VALUES = exports.PERMISSION_ACTIONS = exports.PERMISSION_RESOURCE_VALUES = exports.PERMISSION_RESOURCES = exports.PERMISSION_VALUES = exports.PERMISSIONS = void 0;
exports.isPermissionName = isPermissionName;
/**
 * Права, у которых есть живой потребитель. Их восемь.
 *
 * ⚠ Это не «семь из 81 подключили» — это «прав стало столько, сколько спрашивают»
 * (RBAC-2 §3). 75 удалены миграцией `2026-08-28_rbac_2_3_permission_catalog.sql`, а не
 * оставлены «на будущее»: право без гейта неотличимо от права с гейтом ни в админке, ни
 * в базе, ни в выдаче — значит его отсутствие ничем не сигнализируется и само не
 * всплывёт.
 *
 * ⚠ Седьмое (`conversations:read_all`) заведено RBAC-2.4, восьмое (`content:moderate`) —
 * RBAC-2.9, и оба заведены ВМЕСТЕ со своими гейтами, в одном коммите. Это и есть форма,
 * которой правило §3 требует от каждого следующего: список растёт только с той стороны,
 * где кто-то спрашивает.
 */
exports.PERMISSIONS = {
    /** `core/backend/src/infrastructure/routes/merchantRoutes.ts:698` — гейт `POST /merchants`;
     *  `core/frontend/src/pages/CreateMerchantPage/index.tsx:187` — гейт экрана. */
    MERCHANTS_CREATE: 'merchants:create',
    /** `core/backend/src/application/services/MerchantService.ts:1410` —
     *  `canUserManageMerchant`, платформенная ветка «может управлять любым магазином». */
    MANAGE_MERCHANTS: 'merchants:manage',
    /** `core/backend/src/infrastructure/auth/PermissionService.ts:187` (`canViewProfile`)
     *  и `:262` (`canViewAddresses`) — «сотрудник смотрит чужой профиль/адреса». */
    USERS_READ: 'users:read',
    /** `core/backend/src/infrastructure/auth/PermissionService.ts:212` (`canUpdateProfile`)
     *  и `:237` (`canManageAddresses`). */
    USERS_UPDATE: 'users:update',
    /** `core/backend/src/infrastructure/routes/newsRoutes.ts:127` — `router.use`, гейт
     *  всего поддерева News & Roadmap (HNR-5). */
    MANAGE_NEWS: 'manage_news',
    /** `core/backend/src/infrastructure/routes/merchantMemberRoutes.ts:609` — планка
     *  приглашённого, выданная тиру «личность подтверждена» (RBAC-1.4 Р-1). */
    ACCEPT_MERCHANT_MEMBERSHIP: 'merchant_members:accept',
    /** `messenger/src/presentation/http/middleware/auth.middleware.ts` — `getAccessLevel`
     *  выводит из него уровень `read_all` («читать все переписки платформы»). До RBAC-2.4
     *  это решала строка `roles.includes('support')`; держит право роль `moderator`. */
    CONVERSATIONS_READ_ALL: 'conversations:read_all',
    /**
     * `core/backend/src/infrastructure/routes/reviewRoutes.ts:220` — `router.use`, гейт
     * всего поддерева модерации отзывов и жалоб;
     * `catalog/src/presentation/routes/adminModeration.routes.ts:77,110,164,210` — четыре
     * маршрута разбора заявок мерчантов на бренды и модели товаров.
     *
     * ⚠ ОДНО право на две поверхности в двух сервисах — решение, а не недосмотр (RBAC-2.9,
     * найденное (2)). Продакт назвал ОДНО намерение — «проверять контент», — и два права,
     * которые всегда выдаются вместе и никогда порознь, были бы матрицей в миниатюре:
     * клетки, разложенные по ресурсам, вместо намерения, названного человеком. Понадобится
     * порознь — заведут вторым правом вместе с его гейтом.
     *
     * ⚠ Имя `manage_reviews` НЕ воскрешено намеренно: право с ним существовало, было выдано
     * роли `admin` и не спрашивалось ни одной строкой кода — удалено приговором RBAC-2.3.
     * Оно было ресурсным именем в матричной логике, ровно той, что дала 75 фантомов.
     */
    CONTENT_MODERATE: 'content:moderate',
};
exports.PERMISSION_VALUES = Object.values(exports.PERMISSIONS);
/** Ресурсы, на которые ссылается хоть одно выжившее право. Осиротевших здесь нет. */
exports.PERMISSION_RESOURCES = {
    MERCHANTS: 'merchants',
    USERS: 'users',
    NEWS: 'news',
    MERCHANT_MEMBERS: 'merchant_members',
    CONVERSATIONS: 'conversations',
    /**
     * ⚠ `content` — ресурс, НЕ совпадающий с сервисом. Отзывы живут в Core, заявки на
     * бренды и модели — в Catalog, и это один ресурс, потому что ресурс называет то, ЧТО
     * проверяют, а не то, где оно лежит. Развести его на `reviews` и `catalog` значило бы
     * вернуть матрицу «сервис × действие» (RBAC-2.9, найденное (2)).
     */
    CONTENT: 'content',
};
exports.PERMISSION_RESOURCE_VALUES = Object.values(exports.PERMISSION_RESOURCES);
/** Действия, которые называет хоть одно выжившее право. */
exports.PERMISSION_ACTIONS = {
    CREATE: 'create',
    READ: 'read',
    UPDATE: 'update',
    MANAGE: 'manage',
    ACCEPT: 'accept',
    /**
     * ⚠ `read_all` — отдельное действие, а не `read` над другим ресурсом, и это решение,
     * а не орфография. «Прочитать переписку» и «прочитать ВСЕ переписки платформы» — разные
     * намерения с разной ценой ошибки; свести их к одной клетке значит вернуться к матрице,
     * где объём доступа выводился из пересечения двух списков, а не назывался человеком.
     */
    READ_ALL: 'read_all',
    /**
     * ⚠ `moderate` — отдельное действие, а не `manage` над `content`. «Управлять контентом»
     * (завести новость, отредактировать её) и «проверять чужое» (принять или отклонить
     * заявку мерчанта, скрыть отзыв) — разные намерения с разными гейтами; у первого уже
     * есть `manage_news`. Свести их в одну клетку значило бы отдать держателю проверки
     * право писать, о чём никто не просил.
     */
    MODERATE: 'moderate',
};
exports.PERMISSION_ACTION_VALUES = Object.values(exports.PERMISSION_ACTIONS);
/**
 * Разложение каждого права на пару, которой оно является в базе.
 *
 * `Record<PermissionName, ...>` — не украшение: он делает пропуск строки ошибкой
 * КОМПИЛЯЦИИ. Заводя право выше, нельзя забыть сказать, из чего оно состоит, — а именно
 * так и появлялись осиротевшие `resources` / `actions`.
 *
 * ⚠ Имя НЕ обязано быть `resource:action`: `manage_news` — пара `(news, manage)`,
 * заведённая руками до того, как кто-то развернул матрицу. Денормализация `name` в базе
 * это допускает, и каталог обязан допускать тоже, иначе он опишет не то, что есть.
 */
exports.PERMISSION_PARTS = {
    [exports.PERMISSIONS.MERCHANTS_CREATE]: {
        resource: exports.PERMISSION_RESOURCES.MERCHANTS,
        action: exports.PERMISSION_ACTIONS.CREATE,
    },
    [exports.PERMISSIONS.MANAGE_MERCHANTS]: {
        resource: exports.PERMISSION_RESOURCES.MERCHANTS,
        action: exports.PERMISSION_ACTIONS.MANAGE,
    },
    [exports.PERMISSIONS.USERS_READ]: {
        resource: exports.PERMISSION_RESOURCES.USERS,
        action: exports.PERMISSION_ACTIONS.READ,
    },
    [exports.PERMISSIONS.USERS_UPDATE]: {
        resource: exports.PERMISSION_RESOURCES.USERS,
        action: exports.PERMISSION_ACTIONS.UPDATE,
    },
    [exports.PERMISSIONS.MANAGE_NEWS]: {
        resource: exports.PERMISSION_RESOURCES.NEWS,
        action: exports.PERMISSION_ACTIONS.MANAGE,
    },
    [exports.PERMISSIONS.ACCEPT_MERCHANT_MEMBERSHIP]: {
        resource: exports.PERMISSION_RESOURCES.MERCHANT_MEMBERS,
        action: exports.PERMISSION_ACTIONS.ACCEPT,
    },
    [exports.PERMISSIONS.CONVERSATIONS_READ_ALL]: {
        resource: exports.PERMISSION_RESOURCES.CONVERSATIONS,
        action: exports.PERMISSION_ACTIONS.READ_ALL,
    },
    [exports.PERMISSIONS.CONTENT_MODERATE]: {
        resource: exports.PERMISSION_RESOURCES.CONTENT,
        action: exports.PERMISSION_ACTIONS.MODERATE,
    },
};
/** Объявлено ли это имя каталогом. Для границ, где имя приходит строкой (HTTP-тело). */
function isPermissionName(value) {
    return exports.PERMISSION_VALUES.includes(value);
}
